import { Money, ProductConditionCode } from "../../../common/models/CommonModels";
import { FileModel, Page, QueryBase, enumToArray, Quantity } from "../../../shared/models/models";

export interface CustomerInventoryState {
  query: StockItemsQuery,
  stockItems?: Page<StockItem>;
  stockItem?: StockItem,
  stockItemsUploadResult?: any,
  manufacturers: ManufacturerReference[],
  categories: CategoryReference[],
  groups: StockItemGroupStats[],
  loading: boolean,
  success?: string,
  error: any
}

export interface ChangeStockItemModel {
  groupId: string;
  categoryId: string;
  subcategoryId?: string;
  manufacturerId: string;
  manufacturerName: string;
  number: string;
  alternativeNumber?: string;
  conditionCode: ProductConditionCode;
  availabilityCode: ProductAvailabilityCode;
  quantity: Quantity;
  price?: Money;
  height?: number;
  width?: number;
  depth?: number;
  weight?: number;
  description?: string;
  file?: FileModel;
}

export interface ManufacturerReference {
  id: string;
  name: string;
}

export interface CategoryReference {
  id: string;
  name: string;
}

export interface ProductReference {
  number: string;
  manufacturer: ManufacturerReference;
  category: CategoryReference;
}

export interface SupplierReference {
  id: string;
  name: string;
}

export interface StockItem {
  id: string;
  groupId: string;
  alternativeNumber?: string;
  conditionCode: ProductConditionCode;
  availabilityCode: ProductAvailabilityCode;
  quantity?: Quantity;
  price?: Money;
  width?: number;
  height?: number;
  depth?: number;
  weight?: number;
  description?: string;
  product: ProductReference;
  supplier: SupplierReference;
  file: FileModel | null;
}

export interface InstalledStockItem {
  id: string;
  machineId: string;
  quantity: Quantity;
  inStockQuantity: Quantity;
  stockItem: StockItem;
}

export interface ChangeInstalledStockItem {
  stockItemId: string;
  quantity: number;
}

export interface InternalStockItem {
  id: string;
  alternativeNumber?: string;
  conditionCode: ProductConditionCode;
  installedItemsQuantity: Quantity;
  inStockQuantity: Quantity;
  product: ProductReference;
  files: FileModel[] | null;
}

export interface StockItemsQuery extends QueryBase<StockItemsQueryParameter> {
  groupId?: string;
  manufacturerId?: string;
  categoryId?: string;
  availabilityCode?: ProductAvailabilityCode;
  conditionCodes?: ProductConditionCode[];
}

export interface InstalledStockItemsQuery {
  machineId: string;
  categoryId?: string;
  manufacturerId?: string;
  search?: string;
  sortBy?: InstalledStockItemsQueryParameter;
  sortDesc?: boolean;
}

export interface InternalStockItemsQuery extends QueryBase<InternalStockItemsQueryParameter> {
  machineId: string;
  categoryId?: string;
  manufacturerId?: string;
  conditionCode?: ProductConditionCode;
}

export enum StockItemsQueryParameter {
  name = 1,
  number = 2,
  manufacturerName = 3,
  condition = 4,
  availability = 5,
  price = 6,
  quantity = 7,
  weight = 8,
  supplierName = 9
}

export enum ProductAvailabilityCode {
  inStock = 1,
  in7Days = 2,
  in14Days = 3,
  in1Month = 4
}

export enum InstalledStockItemsQueryParameter {
  number = 1,
  manufacturer = 2
}

export enum InternalStockItemsQueryParameter {
  number = 1,
  manufacturer = 2
}

export const productAvailability = (code: ProductAvailabilityCode, translate: (key: string, options?: any) => string) => {
  switch (code) {
    case ProductAvailabilityCode.inStock:
      return translate("inStock");
    case ProductAvailabilityCode.in7Days:
      return translate("in7Days");
    case ProductAvailabilityCode.in14Days:
      return translate("in14Days");
    case ProductAvailabilityCode.in1Month:
      return translate("in1Month");
    default:
      return "";
  }
};

export const isStockItemsQueryEmpty = (query: StockItemsQuery): boolean => {
  const allConditionCodes = enumToArray<ProductConditionCode>(ProductConditionCode);

  return !query.search &&
    !query.groupId &&
    !query.manufacturerId &&
    !query.categoryId &&
    !query.availabilityCode &&
    (!query.conditionCodes?.length || query.conditionCodes.length === allConditionCodes.length);
};

export interface StockItemGroupStats {
  id: string;
  name: string;
  itemsCount: number;
  isPrivate: boolean;
}

export interface StockBatchUpload {
  groupId: string;
  file: FileModel;
}

export interface InstalledStockItemsStats {
  inUse: Money;
  inStock: Money;
  consumables: Money;
}